import { useDebouncedCallback } from "@mantine/hooks";
import { gtmSendEvent } from "@wojo/analytics";
import { useI18n } from "@wojo/localization";
import { Button, LoadingSkeleton, Text } from "@wojo/ui";
import currencyJs from "currency.js";
import React, { Suspense, useEffect, useId, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { getCurrency } from "../../../../hooks";
import { useTripBuilderQuote } from "../context/TripBuilderQuoteStepContext";
import { usePaymentScheduleSuspenseMutation } from "../hooks";
import styles from "./QuoteAffirm.module.scss";

declare global {
    interface Window {
        affirm?: {
            ui: {
                refresh: () => void;
            };
        };
    }
}

const convertToAffirmFormat = (amount: number) =>
    currencyJs(amount).multiply(100);

export const QuoteAffirm: React.FC = () => {
    const { productPage } = useI18n("productPage");

    return (
        <ErrorBoundary fallback={null}>
            <Suspense
                fallback={
                    <LoadingSkeleton
                        accessibleText={productPage.tripBuilder.paymentLoading.l()}
                        width="100%"
                        height="165"
                    />
                }
            >
                <QuoteAffirmComponent />
            </Suspense>
        </ErrorBoundary>
    );
};

const QuoteAffirmComponent: React.FC = () => {
    const { draftQuote } = useTripBuilderQuote();
    const titleId = useId();
    const ref = useRef<HTMLDivElement>(null);
    const { productPage } = useI18n("productPage");
    const paymentSchedule = usePaymentScheduleSuspenseMutation();
    const grandTotal = draftQuote?.order.grandTotal.value || 0;

    const { formatCurrency } = getCurrency();

    const [affirmHasError, setAffirmHasError] = useState(false);

    const refresh = useDebouncedCallback(() => {
        try {
            // refresh needs to happen after rerender or the button will break
            window.affirm?.ui.refresh();
        } catch {
            !affirmHasError && setAffirmHasError(true);
        }
    }, 300);

    useEffect(() => {
        if (ref.current) {
            refresh();
        }
    });

    if (grandTotal <= 0) {
        return null;
    }

    if (affirmHasError && !paymentSchedule?.autoPayCheckoutOption) {
        return null;
    }

    return (
        <section className={styles["affirm-banner"]} aria-labelledby={titleId}>
            <Text.Heading
                order="6"
                tag="h3"
                id={titleId}
                style={{ marginBottom: "var(--g-spacing-md)" }}
            >
                {productPage.tripBuilder.affirm.heading.l()}
            </Text.Heading>
            {paymentSchedule?.autoPayCheckoutOption && (
                <Text.Body
                    variant="1"
                    emphasis="1"
                    style={{
                        marginBottom: "var(--g-spacing-xs)",
                        minHeight: "1lh",
                    }}
                >
                    {productPage.tripBuilder.affirm.autoPayMessage.l({
                        recurringPayment: formatCurrency(
                            Math.ceil(paymentSchedule.recurringPayment),
                        ),
                        deposit: formatCurrency(
                            paymentSchedule.autoPayCheckoutOption.amount.value,
                        ),
                    })}
                </Text.Body>
            )}
            {!affirmHasError ? (
                <>
                    <Text.Body
                        variant="1"
                        emphasis="1"
                        style={{
                            marginBottom: "var(--g-spacing-xs)",
                            minHeight: "1lh",
                        }}
                    >
                        <span
                            className="affirm-as-low-as"
                            data-amount={convertToAffirmFormat(grandTotal)}
                            data-page-type="banner"
                            ref={ref}
                        >
                            <LoadingSkeleton
                                accessibleText={productPage.tripBuilder.paymentLoading.l()}
                                width="100%"
                                height="1lh"
                            />
                        </span>
                    </Text.Body>
                    <Button
                        type="button"
                        variant="quiet"
                        className="affirm-product-modal"
                        data-amount={convertToAffirmFormat(grandTotal)}
                        data-page-type="banner"
                        onClick={() => {
                            gtmSendEvent({
                                event: "track",
                                ga4Tracking: {
                                    ga4_object: "TRIP_BUILDER",
                                    ga4_action: "MODAL_OPENED",
                                    modalId: "Affirm Info",
                                },
                            });
                        }}
                    >
                        {productPage.tripBuilder.affirm.ctaLabel.l()}
                    </Button>
                </>
            ) : null}
        </section>
    );
};
